import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, ChevronsDown, ChevronsUp, Minus, Plus } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { Button } from '@/components/ui';
import { EXPORT_COLUMNS, exportColumnLabel, type ExportColumnLevel } from '../lib/exportColumns';
import { useExportCollectionLabelsQuery } from './useExportCollectionLabels';

type Move = 'top' | 'up' | 'down' | 'bottom';

/** 선택 항목 순서 바꾸기 — 고른 키들을 묶음째 옮긴다(고도몰 맨위/위/아래/맨아래 버튼과 같음). */
export const moveSelected = (list: ReadonlyArray<string>, picked: ReadonlySet<string>, move: Move): string[] => {
  const out = [...list];
  if (picked.size === 0) return out;
  if (move === 'top' || move === 'bottom') {
    const moving = out.filter((k) => picked.has(k));
    const rest = out.filter((k) => !picked.has(k));
    return move === 'top' ? [...moving, ...rest] : [...rest, ...moving];
  }
  if (move === 'up') {
    for (let i = 1; i < out.length; i++) {
      if (picked.has(out[i]!) && !picked.has(out[i - 1]!)) [out[i - 1], out[i]] = [out[i]!, out[i - 1]!];
    }
  } else {
    for (let i = out.length - 2; i >= 0; i--) {
      if (picked.has(out[i]!) && !picked.has(out[i + 1]!)) [out[i + 1], out[i]] = [out[i]!, out[i + 1]!];
    }
  }
  return out;
};

const listStyle = {
  border: '1px solid var(--divider-strong)',
  background: 'var(--surface-0)',
} as const;

/**
 * QA-433 — 다운로드 항목 고르기. 왼쪽 전체 항목(주문/상품 단위 표시)에서 골라 오른쪽 선택 항목으로 옮기고,
 * 오른쪽에서 순서를 바꾼다. 오른쪽 순서가 곧 엑셀 칸 순서다. 여러 개는 클릭으로 차례로 고른다.
 * QA-468 — 상품 추가입력 라벨(국적·이벤트 옵션 등)은 라벨 목록 API 로 왼쪽 맨 아래에 "[추가입력] 라벨" 로 붙는다.
 *   저장 양식의 productCollectionInput:<라벨> 키는 오른쪽에 같은 이름으로 복원된다.
 */
export const ExportTemplateColumnsEditor = ({
  value,
  onChange,
}: {
  value: ReadonlyArray<string>;
  onChange: (next: string[]) => void;
}) => {
  const { t } = useTranslation('orders');
  const [leftPick, setLeftPick] = useState<Set<string>>(new Set());
  const [rightPick, setRightPick] = useState<Set<string>>(new Set());
  const selected = useMemo(() => new Set(value), [value]);
  const labelsQuery = useExportCollectionLabelsQuery(true);
  const allColumns: ReadonlyArray<{ key: string; level: ExportColumnLevel }> = [
    ...EXPORT_COLUMNS,
    ...(labelsQuery.data ?? []).map((l) => ({ key: l.key, level: 'collection' as const })),
  ];
  const available = allColumns.filter((c) => !selected.has(c.key));
  const labelOf = (key: string) => exportColumnLabel(t, key);
  const toggle = (set: Set<string>, key: string, apply: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    apply(next);
  };

  const add = (keys: ReadonlyArray<string>) => {
    onChange([...value, ...allColumns.map((c) => c.key).filter((k) => keys.includes(k) && !selected.has(k))]);
    setLeftPick(new Set());
  };
  const remove = (keys: ReadonlySet<string>) => {
    onChange(value.filter((k) => !keys.has(k)));
    setRightPick(new Set());
  };

  const row = (key: string, picked: boolean, onClick: () => void, testId: string, level?: ExportColumnLevel) => (
    <li key={key}>
      <button
        type="button"
        onClick={onClick}
        aria-pressed={picked}
        data-testid={testId}
        className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[13px]"
        style={{
          background: picked ? 'var(--surface-1)' : 'transparent',
          color: picked ? 'var(--content-strong)' : 'var(--content-default)',
          fontWeight: picked ? 600 : 400,
        }}
      >
        <span>{labelOf(key)}</span>
        {level === 'item' || level === 'collection' ? (
          <span className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
            {level === 'collection' ? t('orders.exportTemplates.collectionLevel', '추가입력') : t('orders.exportTemplates.itemLevel', '상품별')}
          </span>
        ) : null}
      </button>
    </li>
  );

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_1fr]">
      <div className="flex flex-col gap-1.5">
        <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
          {t('orders.exportTemplates.available', '전체 항목')} ({available.length})
        </span>
        <ul className="h-[320px] overflow-y-auto rounded-md" style={listStyle} aria-label={t('orders.exportTemplates.available', '전체 항목')}>
          {available.map((c) => row(c.key, leftPick.has(c.key), () => toggle(leftPick, c.key, setLeftPick), `tpl-available-${c.key}`, c.level))}
        </ul>
      </div>

      <div className="flex flex-row items-center justify-center gap-1.5 md:flex-col">
        <Button type="button" variant="outline" size="sm" onClick={() => add([...leftPick])} disabled={leftPick.size === 0} aria-label={t('orders.exportTemplates.add', '선택 항목 추가')} data-testid="tpl-add">
          <Plus size={14} aria-hidden="true" />
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => remove(rightPick)} disabled={rightPick.size === 0} aria-label={t('orders.exportTemplates.remove', '선택 항목 빼기')} data-testid="tpl-remove">
          <Minus size={14} aria-hidden="true" />
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => add(available.map((c) => c.key))} disabled={available.length === 0} data-testid="tpl-add-all">
          {t('orders.exportTemplates.addAll', '전체 추가')}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => remove(new Set(value))} disabled={value.length === 0} data-testid="tpl-remove-all">
          {t('orders.exportTemplates.removeAll', '전체 삭제')}
        </Button>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
            {t('orders.exportTemplates.selected', '선택 항목')} ({value.length})
          </span>
          <div className="flex gap-1">
            {([
              ['top', ChevronsUp, '맨 위로'],
              ['up', ChevronUp, '위로'],
              ['down', ChevronDown, '아래로'],
              ['bottom', ChevronsDown, '맨 아래로'],
            ] as const).map(([m, Icon, label]) => (
              <Button
                key={m}
                type="button"
                variant="ghost"
                size="sm"
                disabled={rightPick.size === 0}
                onClick={() => onChange(moveSelected(value, rightPick, m))}
                aria-label={t(`orders.exportTemplates.move.${m}`, label)}
                data-testid={`tpl-move-${m}`}
              >
                <Icon size={14} aria-hidden="true" />
              </Button>
            ))}
          </div>
        </div>
        <ol className="h-[320px] overflow-y-auto rounded-md" style={listStyle} aria-label={t('orders.exportTemplates.selected', '선택 항목')}>
          {value.map((key) => row(key, rightPick.has(key), () => toggle(rightPick, key, setRightPick), `tpl-selected-${key}`))}
        </ol>
      </div>
    </div>
  );
};
